/**
 * Fills a manager field from the user chosen in the employee lookup above it.
 *
 * The CRM access form asks who the access is for and who approves it, and the
 * second answer is almost always the first one's line manager. The directory
 * already knows that, so making the requester look it up and type it is both
 * friction and a source of wrong approvers.
 *
 * ## The rule about not clobbering
 *
 * Auto-filling an approver is a hazard, not a convenience, the moment it
 * overwrites something a person deliberately typed — an access request routed
 * to the wrong approver is exactly the failure this form exists to prevent. So
 * the field is only ever written when:
 *
 *   - it is empty, or
 *   - it still holds, character for character, the value a previous auto-fill
 *     put there.
 *
 * That second case is tracked, not inferred: the hook remembers what it wrote
 * per field. Inferring it — "does the current value look like a name we might
 * have written?" — would eventually guess wrong on someone who typed their
 * manager's name by hand and then changed the user field.
 *
 * A record with no manager writes nothing and clears nothing: the directory not
 * knowing an answer is not a reason to throw away one the user gave.
 *
 * The field stays a plain, editable text input throughout. The form marks it as
 * auto-filled while the value is the hook's own, and `OfferingParameterField`
 * turns that into a note under the control.
 */

import { type Dispatch, type SetStateAction, useCallback, useState } from "react";

import type { DirectoryEntry } from "@/lib/api";
import { logger } from "@/lib/logger";

import type { OfferingParameter } from "../api/contract";
import {
  answerFor,
  type AnswerMap,
  type AnswerState,
} from "../components/offering-parameter-fields";
import { managerSourceParameterId } from "./offering-lookup";

export interface ManagerAutofill {
  /** True while the field still holds the value this hook wrote into it. */
  isAutoFilled: (parameterId: string) => boolean;
  /**
   * Push a directory selection into every manager field that names
   * `sourceParameterId` as its source.
   *
   * Takes the current answers rather than reading them through a state updater
   * so nothing here runs twice under StrictMode; it is only ever called from an
   * event handler, where the render closure is current.
   */
  fillFrom: (
    sourceParameterId: string,
    employee: DirectoryEntry | null,
    answers: AnswerMap,
  ) => void;
  /** Tell the hook the user has changed a field, so it stops owning the value. */
  noteEdit: (parameterId: string, next: AnswerState) => void;
}

export function useManagerAutofill(
  parameters: OfferingParameter[],
  setAnswers: Dispatch<SetStateAction<AnswerMap>>,
): ManagerAutofill {
  /** Field id → the exact string this hook last wrote there. */
  const [written, setWritten] = useState<Record<string, string>>({});

  const fillFrom = useCallback(
    (sourceParameterId: string, employee: DirectoryEntry | null, answers: AnswerMap) => {
      const managerName = employee?.manager_name?.trim() ?? "";
      if (managerName === "") {
        logger.debug("[tickets] no manager on the selected employee", employee?.id);
        return;
      }

      const patch: AnswerMap = {};
      const nowWritten: Record<string, string> = {};

      for (const parameter of parameters) {
        if (managerSourceParameterId(parameter) !== sourceParameterId) {
          continue;
        }
        const id = parameter.parameter_id;
        const current = (answerFor(answers, id).value ?? "").trim();
        if (current !== "" && current !== written[id]) {
          logger.debug("[tickets] leaving the hand-edited manager field alone", id);
          continue;
        }
        patch[id] = { value: managerName, valueIds: [] };
        nowWritten[id] = managerName;
      }

      if (Object.keys(patch).length === 0) {
        return;
      }

      logger.debug("[tickets] manager auto-filled from", sourceParameterId, managerName);
      setAnswers((previous) => ({ ...previous, ...patch }));
      setWritten((previous) => ({ ...previous, ...nowWritten }));
    },
    [parameters, setAnswers, written],
  );

  const noteEdit = useCallback((parameterId: string, next: AnswerState) => {
    setWritten((previous) => {
      const mine = previous[parameterId];
      if (mine === undefined || mine === (next.value ?? "")) {
        return previous;
      }
      const { [parameterId]: _replaced, ...rest } = previous;
      return rest;
    });
  }, []);

  const isAutoFilled = useCallback(
    (parameterId: string) => written[parameterId] !== undefined,
    [written],
  );

  return { isAutoFilled, fillFrom, noteEdit };
}
